//src/pages/HomePage.js
import React, { Suspense, lazy } from "react";
import { useEffect, useState } from "react";
import EntryModal from "../components/EntryModal";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import CategoryCards from "../components/CategoryCards";
import LazyMount from "../components/LazyMount";
import { useSeo } from "../hooks/useSeo";
import { apiRequest } from "../services/api";
import { restoreProductReturnPosition } from "../utils/navigationMemory";

const SaleBanner = lazy(() => import("../components/SaleBanner"));
const CategoryCarousel = lazy(() => import("../components/CategoryCarousel"));
const HomepageProductShowcase = lazy(() =>
  import("../components/HomepageProductShowcase")
);
const HomepageProductShowcaseTwo = lazy(() =>
  import("../components/HomepageProductShowcaseTwo")
);
const Footer = lazy(() => import("../components/Footer"));
const CartDrawer = lazy(() => import("../components/CartDrawer"));
const AddedToBagToast = lazy(() => import("../components/AddedToBagToast"));

function HomePage() {
  
  useSeo("home", null, {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${window.location.origin}/#organization`,
      name: "CHIACA",
      url: window.location.origin,
      logo: `${window.location.origin}/logo-512x512.png`,
      image: `${window.location.origin}/logo-512x512.png`,
      description:
        "CHIACA is a women’s clothing store in Lebanon offering dresses, tops, pants, sets, new arrivals, and stylish fashion pieces with delivery all over Lebanon.",
      sameAs: [],
    },
    {
      "@type": "WebSite",
      "@id": `${window.location.origin}/#website`,
      url: window.location.origin,
      name: "CHIACA | Women’s Clothing Store in Lebanon",
      description:
        "Shop women’s clothing online at CHIACA Lebanon. Discover dresses, tops, pants, sets, new arrivals, and stylish fashion pieces with delivery all over Lebanon.",
      publisher: {
        "@id": `${window.location.origin}/#organization`,
      },
      potentialAction: {
        "@type": "SearchAction",
        target: `${window.location.origin}/products?search={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "ClothingStore",
      "@id": `${window.location.origin}/#store`,
      name: "CHIACA",
      image: `${window.location.origin}/logo-512x512.png`,
      url: window.location.origin,
      description:
        "Women’s clothing store in Lebanon with delivery all over Lebanon.",
      address: {
        "@type": "PostalAddress",
        addressCountry: "LB",
      },
      areaServed: {
        "@type": "Country",
        name: "Lebanon",
      },
      priceRange: "$$",
    },
  ],
});

  const [showEntry, setShowEntry] = useState(false);

 useEffect(() => {
  const trackHomeVisit = () => {
    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "visit_home",
      }),
    }).catch(() => {});
  };

  if ("requestIdleCallback" in window) {
    const idleId = window.requestIdleCallback(trackHomeVisit, {
      timeout: 2500,
    });

    return () => window.cancelIdleCallback?.(idleId);
  }

  const timer = setTimeout(trackHomeVisit, 1200);
  return () => clearTimeout(timer);
}, []);

useEffect(() => {
  const timer = setTimeout(() => {
    restoreProductReturnPosition();
  }, 80);

  return () => clearTimeout(timer);
}, []);

  useEffect(() => {
    const forceLogin = localStorage.getItem("esill-open-login");

    if (forceLogin) {
      setShowEntry(true);
    }
  }, []);

  useEffect(() => {
    const openEntry = () => setShowEntry(true);
    window.addEventListener("open-entry-modal", openEntry);
    return () => window.removeEventListener("open-entry-modal", openEntry);
  }, []);

  const closeEntry = () => {
    localStorage.removeItem("esill-open-login"); // 👈 important
    setShowEntry(false);
  };

  return (
    <div
      className="
  w-full min-h-screen flex flex-col overflow-x-hidden
  bg-[#F6EFE9]
"
    >
      {/* FIXED TOP LAYER */}
      <TopBanner />
      <MainHeader
        onMenuOpen={() => console.log("Menu drawer opens")}
        onSearchOpen={() => console.log("Search opens")}
        onBagOpen={() => console.log("Bag page")}
      />

{/* GLOBAL UI LAYERS */}
<Suspense fallback={null}>
  <CartDrawer />
  <AddedToBagToast />
</Suspense>

      {/* PAGE FLOW */}
      <div className="w-full bg-[#F6EFE9]">
        {/* TOP DISCOVERY */}
        <div className="w-full bg-[#F6EFE9]">
  <CategoryCards />

<LazyMount
  rootMargin="900px"
  placeholderClassName="min-h-[120px]"
>
  <Suspense fallback={null}>
    <div className="mt-2">
      <SaleBanner />
    </div>
  </Suspense>
</LazyMount>

<LazyMount
  rootMargin="900px"
  placeholderClassName="min-h-[170px] md:min-h-[240px]"
>
  <Suspense fallback={null}>
    <div className="mt-1">
      <CategoryCarousel />
    </div>
  </Suspense>
</LazyMount>

<LazyMount
  rootMargin="1000px"
  placeholderClassName="min-h-[520px] md:min-h-[760px]"
>
  <Suspense fallback={null}>
    <div className="mt-1">
      <HomepageProductShowcase />
    </div>
  </Suspense>
</LazyMount>

<LazyMount
  rootMargin="1000px"
  placeholderClassName="min-h-[520px] md:min-h-[760px]"
>
  <Suspense fallback={null}>
    <div className="mt-1">
      <HomepageProductShowcaseTwo />
    </div>
  </Suspense>
</LazyMount>

        </div>

        {/* FOOTER */}
        <LazyMount
  rootMargin="1200px"
  placeholderClassName="min-h-[420px]"
>
  <Suspense fallback={null}>
    <Footer className="mt-auto" />
  </Suspense>
</LazyMount>
      </div>

      {/* ENTRY MODAL */}
      <EntryModal isOpen={showEntry} onClose={closeEntry} />
    </div>
  );
}

export default HomePage;
